import React, { useCallback, useState } from "react";
import { useIntl } from "react-intl";
import { Modal, ModalBody, ModalFooter, Button, Spinner } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import { fetchCanvaConnection } from "../../../../cores/canva/connectApi";
import { CanvaMark } from "../CanvaMark";
import "./CanvaConnectModal.scss";

type Props = {
  isOpen: boolean;
  toggle: () => void;
  /** Tras OAuth ya conectado (o si el backend indica sesión activa). */
  onConnected?: () => void;
};

export const CanvaConnectModal = ({ isOpen, toggle, onConnected }: Props) => {
  const intl = useIntl();
  const [busy, setBusy] = useState(false);

  const handleConnect = useCallback(async () => {
    setBusy(true);
    try {
      const c = await fetchCanvaConnection({ initOAuth: true });
      if (c.oauthUrl) {
        window.location.href = c.oauthUrl;
        return;
      }
      if (c.connected) {
        toggle();
        onConnected?.();
      } else {
        toast.error(
          intl.formatMessage({ id: "app.canvaConnect.oauthUrlMissing" })
        );
      }
    } catch (e: any) {
      toast.error(
        e?.response?.data?.message ||
          intl.formatMessage({ id: "app.canvaConnect.backendError" })
      );
    } finally {
      setBusy(false);
    }
  }, [intl, onConnected, toggle]);

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      className="canva-connect-modal-dialog"
      modalClassName="canva-connect-modal"
      backdropClassName="canva-connect-modal__backdrop"
      zIndex={2000}
      centered
      backdrop="static"
    >
      <div className="canva-connect-modal__header">
        <div className="canva-connect-modal__header-brand">
          <CanvaMark size={28} />
          <span className="canva-connect-modal__header-title">
            {intl.formatMessage({ id: "app.canvaConnectModal.brandName" })}
          </span>
        </div>
        <button
          type="button"
          className="canva-connect-modal__close"
          onClick={toggle}
          aria-label={intl.formatMessage({ id: "app.canvaConnectModal.close" })}
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </div>

      <ModalBody className="canva-connect-modal__body">
        <div className="canva-connect-modal__hero">
          <div className="canva-connect-modal__blob" aria-hidden />
          <CanvaMark size={72} />
        </div>
        <h2 className="canva-connect-modal__headline">
          {intl.formatMessage({ id: "app.canvaConnectModal.headline" })}
        </h2>
        <p className="canva-connect-modal__text">
          {intl.formatMessage({ id: "app.canvaConnectModal.descriptionLine1" })}
        </p>
        <p className="canva-connect-modal__text canva-connect-modal__text--muted">
          {intl.formatMessage({ id: "app.canvaConnectModal.descriptionLine2" })}
        </p>

        <div className="canva-connect-modal__actions">
          <Button
            type="button"
            color="light"
            className="canva-connect-modal__btn-connect"
            onClick={handleConnect}
            disabled={busy}
          >
            {busy ? (
              <Spinner size="sm" />
            ) : (
              <>
                <CanvaMark size={22} />
                <span>
                  {intl.formatMessage({ id: "app.canvaConnectModal.connect" })}
                </span>
              </>
            )}
          </Button>
        </div>

        <p className="canva-connect-modal__footnote">
          {intl.formatMessage({ id: "app.canvaConnectModal.disclaimer" })}
        </p>
      </ModalBody>

      <ModalFooter className="canva-connect-modal__footer">
        <Button
          type="button"
          className="canva-connect-modal__btn-accept"
          onClick={toggle}
        >
          {intl.formatMessage({ id: "app.canvaConnectModal.accept" })}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
